import { OnDataOptions } from '@apollo/client';
import { useIonAlert } from '@ionic/react';
import { sortBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeyType } from '../../../i18n';
import {
  RegisterAndSubscribeToMachineBindingSubscription,
  useRegisterAndSubscribeToMachineBindingSubscription,
} from '../../graphql/generated';
import { useUserProvider } from '../../user.provider';

export interface MachineAlertBindingContext {
  /**
   * Optional identifier of entity reading the results
   */
  readById?: string;
  /**
   * Rate (millis) at which to emit alerts.
   *
   * Minimum is 500ms.
   */
  rate?: number;
  /**
   * The max number of alert messages to publish at a time.
   *
   * Range is 1 to 100.
   * Default is 100.
   */
  bufferSize?: number;
}

export interface MachineAlertMessage {
  id: string;
  code: string;
  message: string;
  timestamp: number;
}

export interface MachineAlertBindingResult {
  messages: MachineAlertMessage[];
}

export interface MachineAlert {
  code: MachineAlertEnum;
  title: i18nKeyType;
  subtitle?: i18nKeyType;
  description: i18nKeyType;
}

export enum MachineAlertEnum {}
export const MachineAlertLookup: Record<MachineAlertEnum, MachineAlert> = {};

const useMachineAlertsBinding = (
  machineId: string,
  context: MachineAlertBindingContext
): MachineAlertMessage[] => {
  const [alerts, setAlerts] = useState<MachineAlertMessage[]>([]);

  useRegisterAndSubscribeToMachineBindingSubscription({
    variables: {
      input: {
        machineId,
        binding: {
          namespace: 'machine',
          type: 'alerts',
          machineId,
          context,
        },
      },
    },
    onData: (
      options: OnDataOptions<RegisterAndSubscribeToMachineBindingSubscription>
    ) => {
      const data = options?.data?.data
        ?.registerAndSubscribeToMachineBindingResult
        ?.data as MachineAlertBindingResult;

      setAlerts(data?.messages ?? []);
    },
  });

  return alerts;
};

const useMachineAlerts = (machineId: string) => {
  const { userInfo } = useUserProvider();

  const context = useMemo<MachineAlertBindingContext>(() => {
    return {
      readById: userInfo?.userId,
      rate: 500,
      bufferSize: 1,
    };
  }, [userInfo]);

  const alerts = useMachineAlertsBinding(machineId, context);

  const [showAlert, dismissAlert] = useIonAlert();

  const { i18n } = useTranslation();

  useEffect(() => {
    dismissAlert();
  }, [dismissAlert]);

  useEffect(() => {
    if (alerts.length > 0) {
      const machineAlert = sortBy(alerts, 'timestamp').pop();
      const code = machineAlert?.code;
      const alert = MachineAlertLookup[code as unknown as MachineAlertEnum];

      const title = alert?.title ? i18n.t(alert.title) : code;
      const subtitle = alert?.subtitle ? i18n.t(alert.subtitle) : undefined;
      const description = alert?.description
        ? i18n.t(alert.description)
        : machineAlert?.message;

      showAlert({
        header: title,
        subHeader: subtitle,
        message: description,
        buttons: [i18n.t('common:ok')],
      });
    }
  }, [alerts, showAlert, i18n]);
};

export { useMachineAlertsBinding, useMachineAlerts };
